import * as React from 'react'
import { useEffect, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'

import type { BillType } from '../../../all-types/bill'
import { BillPrinter } from '../../printer'
import { getFormattedDate, getFormattedTime } from '../../../utils'
import { ORDER_TYPE, USER_ROLES } from '../../../all-types'
import { deleteBillAPI, updateBillPaymentMode } from '../../../services'
import { useAppDispatch } from '../../../redux-store/hooks'
import { setSelectedTable } from '../../../redux-store'
import { TakeAwayTableMock } from '../../../constants'
import { useNavigate } from 'react-router-dom'
import { CommonBtn } from '../../../components'

type OrderDetailProps = {
  selectedBill: BillType | null
  setSelectedBill: (bill: BillType) => void
  refresh: () => void
}

type Mode = 'CASH' | 'CARD' | 'UPI'

function OrderDetail({
  selectedBill,
  setSelectedBill,
  refresh,
}: OrderDetailProps) {
  const theme = useTheme()

  // Local UI state for dropdown + saving flag
  const [mode, setMode] = useState<Mode>('CASH')
  const [saving, setSaving] = useState(false)
  const dispatcher = useAppDispatch()
  const navigate = useNavigate()
  const showEditAndDeleteOption =
    localStorage.getItem('ruchi_role') === USER_ROLES.ADMIN

  // Keep dropdown synced with the currently selected bill
  useEffect(() => {
    const initial = (
      selectedBill?.modeOfPayment || 'CASH'
    ).toUpperCase() as Mode
    setMode(initial)
  }, [selectedBill?._id, selectedBill?.modeOfPayment])

  const onModeChange = (e: SelectChangeEvent) => {
    setMode(e.target.value as Mode)
  }

  const onUpdateClick = async () => {
    if (!selectedBill?._id) return
    setSaving(true)
    try {
      const response = await updateBillPaymentMode(selectedBill._id, mode)
      console.log(response)
      setSelectedBill(response)
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  const modifyBill = () => {
    try {
      if (!selectedBill) return
      TakeAwayTableMock.tableName = 'Modify Bill'
      TakeAwayTableMock.tableNo = Number(selectedBill.info.tableNo)
      dispatcher(
        setSelectedTable({ ...TakeAwayTableMock, billSession: selectedBill })
      )
      navigate('/app/counter?tab=modify-bill')
    } catch (e) {
      console.log(e)
    }
  }

  const deleteBill = async () => {
    try {
      if (!selectedBill?._id) return
      await deleteBillAPI(selectedBill._id)
      console.log('Delete Bill: ', selectedBill._id)
    } catch (e) {
      console.log(e)
    } finally {
      refresh()
    }
  }

  if (!selectedBill) {
    return (
      <>
        <Divider />
        <Box color={theme.palette.offline.main} pr={3} mt={3}>
          Select an order to view details.
        </Box>
      </>
    )
  }

  const items = selectedBill.items || []

  return (
    <Paper
      elevation={3}
      sx={{
        mt: { xs: 10, md: 5 },
        p: 4,
        borderRadius: 3,
        backgroundColor: 'background.paper',
        minWidth: { xs: 300, sm: 400, lg: 500 },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Typography fontWeight={600} fontSize={18}>
            Serial No. {selectedBill.sNo}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography
              sx={{ color: theme.palette.offline.main, fontSize: 12 }}
            >
              {getFormattedDate(Number(selectedBill.timeInfo.timestamp))}
            </Typography>
            <Typography
              sx={{ color: theme.palette.offline.main, fontSize: 12 }}
            >
              {getFormattedTime(Number(selectedBill.timeInfo.timestamp))}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          disableElevation
          disableRipple
          sx={{
            bgcolor:
              selectedBill.orderType === ORDER_TYPE.DINE_IN
                ? theme.palette.available.main
                : 'primary.main',
            color: theme.palette.color12.main,
            fontWeight: 600,
            px: 2,
            py: 0.5,
            borderRadius: 2,
            textTransform: 'none',
          }}
        >
          {selectedBill.orderType === ORDER_TYPE.DINE_IN ? 'Done' : `Pending`}
        </Button>

        {showEditAndDeleteOption && (
          <CommonBtn
            variant="outlined"
            onClick={modifyBill}
            sx={{ textTransform: 'none' }}
            color="warning"
          >
            Edit Bill
          </CommonBtn>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box mb={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography fontWeight={500} fontSize={16} mb={1}>
            Details
          </Typography>
          <Typography fontWeight={600} fontSize={16} mb={1}>
            Bill No. {selectedBill.billNo}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          sx={{ fontSize: 12 }}
        >
          <Stack spacing={0.5}>
            <Typography variant="body2" color={theme.palette.offline.main}>
              Table
            </Typography>
            <Typography
              fontWeight={600}
              sx={{
                color: 'GrayText',
                fontSize: 14,
                textAlign: 'center',
              }}
            >
              {selectedBill.info.tableNo}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" color={theme.palette.offline.main}>
              Items
            </Typography>
            <Typography
              fontWeight={600}
              sx={{ color: 'GrayText', fontSize: 14, textAlign: 'center' }}
            >
              {selectedBill.items.length}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" color={theme.palette.offline.main}>
              Order Type
            </Typography>
            <Typography
              fontWeight={600}
              sx={{
                color: 'GrayText',
                fontSize: 14,
                textAlign: 'center',
                textTransform: 'lowercase',
                '&::first-letter': { textTransform: 'capitalize' },
              }}
            >
              {selectedBill?.orderType || 'Dine In'}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" color={theme.palette.offline.main}>
              Payment mode
            </Typography>
            <Typography
              fontWeight={600}
              sx={{
                color: 'GrayText',
                fontSize: 14,
                textTransform: 'lowercase',
                '&::first-letter': {
                  textTransform: 'capitalize',
                },
                textAlign: 'center',
              }}
            >
              {selectedBill.modeOfPayment}
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* ---- Payment Mode Update Controls (dropdown + button) ---- */}
      <Box
        mt={1}
        display="flex"
        gap={1.5}
        alignItems="center"
        justifyContent="flex-end"
      >
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="payment-mode-label">Payment</InputLabel>
          <Select
            labelId="payment-mode-label"
            label="Payment"
            value={mode}
            onChange={onModeChange}
          >
            <MenuItem value="CASH">Cash</MenuItem>
            <MenuItem value="CARD">Card</MenuItem>
            <MenuItem value="UPI">UPI</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={onUpdateClick}
          disabled={!selectedBill || saving}
        >
          {saving ? 'Updating…' : 'Update'}
        </Button>
      </Box>
      {/* --------------------------------------------------------- */}

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography fontWeight={500} fontSize={16} mb={0.5}>
          Order Info:
          <Box
            component="span"
            sx={{
              pl: 1,
              color: theme.palette.offline.main,
            }}
          >
            {selectedBill?.takeAwayType === 'NONE'
              ? ''
              : selectedBill?.takeAwayType}
          </Box>
        </Typography>
        <Stack spacing={2}>
          {items.map((item) => (
            <Box key={item.productId}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%', mb: 1 }}
              >
                <Box display="flex" gap={2}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      sx={{
                        color: theme.palette.offline.main,
                        fontSize: 13,
                        mb: 1,
                      }}
                    >
                      Items
                    </Typography>
                    <Box position="relative">
                      <Avatar
                        src={item.itemImage}
                        sx={{ width: 32, height: 32 }}
                      />
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: -5,
                          right: -5,
                          fontFamily: 'monospace',
                          fontSize: 10,
                          fontWeight: 600,
                          borderRadius: '50%',
                        }}
                      >
                        x{item.noOfItems}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    fontWeight={600}
                    sx={{
                      fontSize: 14,
                      color: 'graytext',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'end',
                      textTransform: 'lowercase',
                    }}
                  >
                    {item.itemName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    sx={{
                      color: theme.palette.offline.main,
                      fontSize: 13,
                      mb: 1,
                    }}
                  >
                    Price
                  </Typography>
                  <Typography
                    fontWeight={600}
                    sx={{ color: 'GrayText', fontSize: 13 }}
                  >
                    ₹{item.mrp * item.noOfItems}
                  </Typography>
                </Box>
              </Box>
              <Divider />
            </Box>
          ))}
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt="5px"
        >
          <Typography fontSize={11} fontWeight={500}>
            Total Price
          </Typography>
          <Typography fontSize={11} fontWeight={500}>
            {selectedBill.billingInfo.totalItemsCostRaw}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt="5px"
        >
          <Typography fontSize={11} fontWeight={500}>
            Tax
          </Typography>
          <Typography fontSize={11} fontWeight={500}>
            +{selectedBill.billingInfo.totalTaxCharged}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt="5px"
        >
          <Typography fontSize={11} fontWeight={500}>
            Discount
          </Typography>
          <Typography fontSize={11} fontWeight={500}>
            -{selectedBill.billingInfo.totalDiscountIssued}
          </Typography>
        </Box>
        <Divider sx={{ color: theme.palette.color12.main }} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt="5px"
        >
          <Typography fontSize={15} fontWeight={600}>
            Grand Total
          </Typography>
          <Typography fontSize={15} fontWeight={600}>
            ₹{selectedBill.billingInfo.finalBillAmount}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <BillPrinter bill={selectedBill} />
        {showEditAndDeleteOption && (
          <CommonBtn
            variant="contained"
            onClick={deleteBill}
            sx={{ textTransform: 'none' }}
            color="error"
          >
            Delete Bill
          </CommonBtn>
        )}
      </Box>
    </Paper>
  )
}

export { OrderDetail }
